import { ResumeData } from '../../types';
import { renderMarkdown } from '../../utils/markdownRenderer';

interface ATSTemplateProps {
    data: ResumeData;
}

const formatSectionContent = (content: any): string => {
    if (!content) return '';
    if (typeof content === 'string') {
        return content;
    }
    if (Array.isArray(content)) {
        return content
            .map(entry => {
                if (!entry) return '';
                if (typeof entry === 'string') return entry;
                const title = entry.title || entry.name || '';
                const url = entry.url ? ` (${entry.url})` : '';
                return `${title}${url}`.trim();
            })
            .filter(Boolean)
            .join(' · ');
    }
    if (typeof content === 'object') {
        const title = content.title || content.name || '';
        const url = content.url ? ` (${content.url})` : '';
        return `${title}${url}`.trim();
    }
    return String(content);
};

export function ATSTemplate({ data }: ATSTemplateProps) {
    const {
        header,
        summary,
        experience,
        education,
        skills,
        projects,
        settings,
        customSections,
        courses,
        languages,
        awards,
    } = data;
    const skillsCategories = skills?.categories || [];
    const coursesList = courses || [];
    const spacing = settings.compactSpacing ? 'compact' : 'normal';

    return (
        <div className={`p-12 font-sans text-black ${spacing === 'compact' ? 'text-[10pt]' : 'text-[11pt]'}`}>
            {/* Header */}
            <div className="text-center mb-4">
                <h1 className="text-2xl font-bold mb-1">{header.fullName || 'Your Name'}</h1>
                <div className="text-sm">
                    {[header.email, header.phone, header.location].filter(Boolean).join(' | ')}
                </div>
                {header.links && header.links.length > 0 && (
                    <div className="text-sm">
                        {header.links.map((link, i) => (
                            <span key={i}>
                                {i > 0 && ' | '}
                                {link.url ? (
                                    <a
                                        href={link.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="underline"
                                    >
                                        {link.label || link.url}
                                    </a>
                                ) : (
                                    link.label
                                )}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Summary */}
            {summary && (
                <div className={spacing === 'compact' ? 'mb-3' : 'mb-4'}>
                    <h2 className="text-sm font-bold uppercase border-b border-black pb-1 mb-2">Summary</h2>
                    <div className="text-justify whitespace-pre-line">{renderMarkdown(summary)}</div>
                </div>
            )}

            {/* Experience */}
            {experience && experience.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-3' : 'mb-4'}>
                    <h2 className="text-sm font-bold uppercase border-b border-black pb-1 mb-2">Experience</h2>
                    {experience.map((job, index) => (
                        <div key={job.id} className={index > 0 ? (spacing === 'compact' ? 'mt-2' : 'mt-3') : ''}>
                            <div className="flex justify-between items-baseline">
                                <div className="font-bold">
                                    {job.position} | {job.company}
                                </div>
                            </div>
                            <div className="text-sm italic">
                                {job.location && `${job.location} | `}
                                {job.startDate} - {job.current ? 'Present' : job.endDate || 'Present'}
                            </div>
                            {job.bullets && job.bullets.length > 0 && (
                                <ul className="list-disc ml-5 mt-1">
                                    {job.bullets.map((bullet, i) => (
                                        <li key={i} className="mb-0.5">{bullet}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Education */}
            {education && education.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-3' : 'mb-4'}>
                    <h2 className="text-sm font-bold uppercase border-b border-black pb-1 mb-2">Education</h2>
                    {education.map((edu, index) => (
                        <div key={edu.id} className={index > 0 ? 'mt-2' : ''}>
                            <div className="font-bold">{edu.institution}</div>
                            <div>
                                {edu.degree}{edu.field && ` in ${edu.field}`}
                            </div>
                            {(edu.graduationDate || edu.gpa) && (
                                <div className="text-sm italic">
                                    {[edu.graduationDate, edu.gpa && `GPA: ${edu.gpa}`].filter(Boolean).join(' | ')}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Skills */}
            {skillsCategories.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-3' : 'mb-4'}>
                    <h2 className="text-sm font-bold uppercase border-b border-black pb-1 mb-2">Skills</h2>
                    {skillsCategories.map((category, index) => {
                        const items = category.items || [];
                        return (
                            <div key={index} className="mb-1">
                                <span className="font-bold">{category.name}:</span> {items.join(', ')}
                            </div>
                        );
                    })}
                </div>
            )}

            {coursesList.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-3' : 'mb-4'}>
                    <h2 className="text-sm font-bold uppercase border-b border-black pb-1 mb-2">Courses</h2>
                    {coursesList.map(course => (
                        <div key={course.id} className="mb-2">
                            <div className="font-bold">
                                {course.course}
                                {course.url && (
                                    <span className="ml-1">
                                        <a href={course.url} target="_blank" rel="noreferrer" className="underline">
                                            {course.institution || course.url}
                                        </a>
                                    </span>
                                )}
                            </div>
                            {course.description && <div className="text-sm whitespace-pre-line">{renderMarkdown(course.description)}</div>}
                            <div className="text-xs text-gray-700">
                                {[course.institution, [course.startDate, course.endDate].filter(Boolean).join(' - ')].filter(Boolean).join(' | ')}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {languages && languages.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-3' : 'mb-4'}>
                    <h2 className="text-sm font-bold uppercase border-b border-black pb-1 mb-2">Languages</h2>
                    {languages.map(language => (
                        <div key={language.id} className="mb-2">
                            <div className="font-bold">{language.language}</div>
                            {language.level && <p className="text-xs">{language.level}</p>}
                        </div>
                    ))}
                </div>
            )}

            {awards && awards.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-3' : 'mb-4'}>
                    <h2 className="text-sm font-bold uppercase border-b border-black pb-1 mb-2">Awards & Certifications</h2>
                    {awards.map(award => (
                        <div key={award.id} className="mb-2">
                            <div className="font-bold">
                                {award.title}
                                {award.url && (
                                    <span className="ml-1">
                                        <a href={award.url} target="_blank" rel="noreferrer" className="underline">
                                            {award.organization || award.url}
                                        </a>
                                    </span>
                                )}
                            </div>
                            {(award.organization || award.date) && (
                                <div className="text-xs text-gray-700">
                                    {[award.organization, award.date].filter(Boolean).join(' | ')}
                                </div>
                            )}
                            {award.description && <div className="text-sm whitespace-pre-line">{renderMarkdown(award.description)}</div>}
                        </div>
                    ))}
                </div>
            )}

            {/* Projects */}
            {projects && projects.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-3' : 'mb-4'}>
                    <h2 className="text-sm font-bold uppercase border-b border-black pb-1 mb-2">Projects</h2>
                    {projects.map((project, index) => (
                        <div key={project.id} className={index > 0 ? 'mt-2' : ''}>
                            <div className="font-bold">
                                {project.url ? (
                                    <a href={project.url} target="_blank" rel="noreferrer" className="underline">
                                        {project.name || project.url}
                                    </a>
                                ) : (
                                    project.name
                                )}
                            </div>
                            <div className="whitespace-pre-line">{renderMarkdown(project.description)}</div>
                            {project.highlights && project.highlights.length > 0 && (
                                <ul className="list-disc ml-5 mt-1">
                                    {project.highlights.map((highlight, i) => (
                                        <li key={i} className="mb-0.5">{highlight}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Additional Sections */}
            {customSections && customSections.length > 0 && (
                <div className={spacing === 'compact' ? 'mb-3' : 'mb-4'}>
                    <h2 className="text-sm font-bold uppercase border-b border-black pb-1 mb-2">Additional Sections</h2>
                    {customSections.map((section, index) => {
                        const sectionContent = formatSectionContent(section.content);
                        return (
                            <div key={section.id} className={index > 0 ? (spacing === 'compact' ? 'mt-2' : 'mt-3') : ''}>
                                <div className="font-bold">
                                    {section.title}
                                    {section.url && (
                                        <span className="ml-1">
                                            <a href={section.url} target="_blank" rel="noreferrer" className="underline">
                                                {section.url}
                                            </a>
                                        </span>
                                    )}
                                </div>
                                {section.location && (
                                    <div className="text-xs text-gray-600">{section.location}</div>
                                )}
                                {(section.startDate || section.endDate) && (
                                    <div className="text-xs text-gray-600">
                                        {[section.startDate, section.endDate].filter(Boolean).join(' - ')}
                                    </div>
                                )}
                                {sectionContent && (
                                    <div className="text-sm whitespace-pre-line">{renderMarkdown(sectionContent)}</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
